import { useState } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { createCampaign, updateCampaignRecipients } from '@/services/db/campaign/mutations';
import { createNewsletter, FULL_SEND_PERCENTAGE, updateNewsletterRecipients } from '@/services/db/newsletter/mutations';
import { campaignHref, newsletterHref } from '../item/usage-links';
import {
  campaignCreateFields,
  campaignTriggerUpdate,
  newSegmentHref,
  newsletterRecipientsUpdate,
} from '../utils/create-from-item';
import type { DataIndexItemRef } from '../utils/item';
import type { CreatableTarget } from '../utils/use-in-actions';

/**
 * Making something out of a data-index item.
 *
 * A segment is a link — the builder reads its conditions from the URL. An
 * automation and a one-time send are two writes each: create the record,
 * then tell it what it is about. Each opens in a new tab, as the page
 * this replaces does, so the item being read is not lost.
 */

export type CreateFromItem = {
  run: (target: CreatableTarget) => Promise<void>;
  /** Which one is in flight, so the menu can show it and refuse a second. */
  pending: CreatableTarget | null;
};

/** Where a made record lives, and whether the second write landed. */
type Made = { href: string; isComplete: boolean };

/**
 * The three things done to the tab that opens. A `Window` has them, so
 * `window.open` needs no adapting; naming them keeps the hook honest
 * about what it relies on.
 */
export type OpenedTab = {
  opener: unknown;
  location: { href: string };
  close: () => void;
};

/** Opens the tab a record will be shown in. Overridable in tests. */
const openBlankTab = (): OpenedTab | null => window.open('', '_blank');

export function useCreateFromItem(
  workspaceId: number,
  item: DataIndexItemRef,
  openTab: () => OpenedTab | null = openBlankTab,
): CreateFromItem {
  const snackbars = useSnackbars();
  const [pending, setPending] = useState<CreatableTarget | null>(null);

  const run = async (target: CreatableTarget) => {
    if (pending != null) {
      return;
    }

    // Opened now, before anything is awaited: a browser lets a page open a
    // tab only while it is still handling the click, and the writes below
    // outlast that. The page this replaces opens it after them, so Safari
    // and Firefox block it there. The tab is pointed at the record once it
    // exists, and closed if it never does.
    const tab = openTab();
    if (tab != null) {
      // It will show this app's own pages, but nothing on them needs a
      // handle back to the page that opened them.
      tab.opener = null;
    }

    setPending(target);
    try {
      const made = await make(target, workspaceId, item);
      show(tab, made.href, target);
      if (!made.isComplete) {
        // The record exists, so a retry would make a second one. Say what
        // happened instead of that it failed, and leave it open to finish.
        snackbars.create({
          id: `data-index.create.${target}.incomplete`,
          variant: 'critical',
          message: `The ${NOUN[target]} was created, but its ${SECOND_WRITE[target]} could not be set. Finish setting it up in the tab that opened.`,
        });
      }
    } catch {
      tab?.close();
      snackbars.create({
        id: `data-index.create.${target}.error`,
        variant: 'critical',
        message: `There was an error creating the ${NOUN[target]}. Try again.`,
      });
    } finally {
      setPending(null);
    }
  };

  /**
   * Sends the opened tab to the record, or — where the browser refused
   * even that tab — says where the record is, so the click never ends in
   * nothing and invites a second one.
   */
  const show = (tab: OpenedTab | null, href: string, target: CreatableTarget) => {
    if (tab != null) {
      tab.location.href = href;
      return;
    }
    snackbars.create({
      id: `data-index.create.${target}.blocked`,
      variant: 'success',
      message: `The ${NOUN[target]} is ready.`,
      action: { label: 'Open it', href, isExternal: true },
    });
  };

  return { run, pending };
}

/** What a message calls the thing being made. */
const NOUN = {
  segment: 'segment',
  campaign: 'automation',
  newsletter: 'one-time send',
} satisfies Record<CreatableTarget, string>;

/** What the second write sets, where there is one. */
const SECOND_WRITE = {
  segment: 'conditions',
  campaign: 'trigger',
  newsletter: 'recipients',
} satisfies Record<CreatableTarget, string>;

/**
 * Makes the record. Throws only when there is nothing to show for it —
 * a second write that fails still answers with where the record is.
 */
async function make(target: CreatableTarget, workspaceId: number, item: DataIndexItemRef): Promise<Made> {
  if (target === 'segment') {
    return { href: newSegmentHref(workspaceId, item), isComplete: true };
  }
  if (target === 'campaign') {
    return makeCampaign(workspaceId, item);
  }
  return makeNewsletter(workspaceId, item);
}

async function makeCampaign(workspaceId: number, item: DataIndexItemRef): Promise<Made> {
  const { campaign } = await createCampaign({ workspaceId, campaign: campaignCreateFields(item) });
  const href = campaignHref(workspaceId, Number(campaign.id));

  // An event is triggered by its own create; everything else is told
  // separately, because a `seg_attr` trigger needs a saved record.
  const trigger = campaignTriggerUpdate(item);
  if (trigger == null) {
    return { href, isComplete: true };
  }

  return settle(href, updateCampaignRecipients({ workspaceId, campaignId: String(campaign.id), campaign: trigger }));
}

async function makeNewsletter(workspaceId: number, item: DataIndexItemRef): Promise<Made> {
  const newsletter = await createNewsletter({
    workspaceId,
    newsletter: { name: `${item.id} attribute triggered one-time send`, send_percentage: FULL_SEND_PERCENTAGE },
  });
  const href = newsletterHref(workspaceId, Number(newsletter.id));

  return settle(
    href,
    updateNewsletterRecipients({
      workspaceId,
      newsletterId: newsletter.id,
      // The created send carries the sending options the backend just set,
      // and a recipients update has to restate them.
      newsletter: newsletterRecipientsUpdate(item, newsletter),
    }),
  );
}

/** Waits on the second write, reporting its failure rather than throwing it. */
async function settle(href: string, secondWrite: Promise<unknown>): Promise<Made> {
  try {
    await secondWrite;
    return { href, isComplete: true };
  } catch {
    return { href, isComplete: false };
  }
}
